import { useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { HpButton, HpCardHeader, TableScroll } from '@hp/ui';
import { trpc } from '../../api/trpc';
import { StatusBadge } from '../../components/badges';
import { ErrorAlert } from '../../components/error-alert';
import { LoadMore } from '../../components/ui/load-more';
import { BrutTable, BrutTheadRow } from '../../components/ui/table';
import { RelativeTime } from '../../components/relative-time';
import { PdLink } from '../../components/pd-link';
import { invalidateSelfservice, useSelfserviceTokens, type SelfserviceToken } from './queries';

// --- Links (token metadata; the URL secret is shown only at creation, never here) ---

export function LinksTable({ canManage, pdDomain }: { canManage: boolean; pdDomain: string | null | undefined }) {
  const { t } = useTranslation(['contactUpdates', 'common']);
  const tokens = useSelfserviceTokens();
  const items = useMemo(() => tokens.data?.pages.flatMap((p) => p.items) ?? [], [tokens.data]);

  const revoke = useMutation(trpc.selfservice.revokeLink.mutationOptions({ onSuccess: invalidateSelfservice }));

  function onRevoke(id: number) {
    if (revoke.isPending) return;
    if (!window.confirm(t('links.revokeConfirm'))) return;
    revoke.mutate({ id });
  }

  const colCount = canManage ? 7 : 6;

  return (
    <section className="hp-card">
      <HpCardHeader title={t('links.title')} subtitle={t('links.subtitle')} icon="link" />
      <ErrorAlert error={tokens.error ?? revoke.error} className="bo-alert--inset" />
      <TableScroll label={t('links.tableLabel')}>
        <BrutTable>
          <BrutTheadRow>
            <th>{t('common:table.created')}</th>
            <th>{t('links.person')}</th>
            <th>{t('links.campaign')}</th>
            <th>{t('common:table.status')}</th>
            <th>{t('links.expires')}</th>
            <th>{t('links.used')}</th>
            {canManage && <th />}
          </BrutTheadRow>
          <tbody>
            {items.map((token: SelfserviceToken) => (
              <tr key={token.id}>
                <td className="bo-nowrap">
                  <RelativeTime value={token.createdAt} />
                </td>
                <td className="bo-nowrap">
                  <span className="bo-row">
                    <strong>{token.personName ?? '—'}</strong>
                    <PdLink domain={pdDomain} entity="person" pdId={token.pdPersonId} />
                  </span>
                </td>
                <td>
                  {token.campaign ? (
                    <span className="bo-chip">{token.campaign}</span>
                  ) : (
                    <span className="bo-muted">—</span>
                  )}
                </td>
                <td>
                  <StatusBadge status={token.status} />
                </td>
                <td className="bo-nowrap">
                  <RelativeTime value={token.expiresAt} />
                </td>
                <td className="bo-nowrap">
                  <RelativeTime value={token.usedAt} />
                </td>
                {canManage && (
                  <td>
                    {token.status === 'active' && (
                      <HpButton
                        className="bo-button--sm bo-danger"
                        busy={revoke.isPending && revoke.variables?.id === token.id}
                        disabled={revoke.isPending}
                        onClick={() => onRevoke(token.id)}
                      >
                        {t('links.revoke')}
                      </HpButton>
                    )}
                  </td>
                )}
              </tr>
            ))}
            {!tokens.isLoading && items.length === 0 && (
              <tr>
                <td colSpan={colCount} className="bo-cell-empty">
                  {t('links.empty')}
                </td>
              </tr>
            )}
            {tokens.isLoading && (
              <tr>
                <td colSpan={colCount} className="bo-cell-empty">
                  {t('common:actions.loading')}
                </td>
              </tr>
            )}
          </tbody>
        </BrutTable>
      </TableScroll>
      {tokens.hasNextPage && (
        <div className="hp-card__body bo-row">
          <LoadMore
            hasNextPage={tokens.hasNextPage}
            isFetchingNextPage={tokens.isFetchingNextPage}
            onClick={() => void tokens.fetchNextPage()}
          />
        </div>
      )}
    </section>
  );
}
